'use client';

import { useEffect, useRef } from 'react';
import { useStore } from '@/store/store';
import { useGlobalContext } from '@/store/contexts/global-context';

const RETRY_INTERVAL_MS = 500;
const MAX_ATTEMPTS = 20;

export const useBrazeUserProfile = () => {
    const accountID = useStore((state) => state.userContext?.accountSettings?.accountID);
    const emailAddress = useStore((state) => state.userContext?.accountSettings?.emailAddress);
    const userName = useStore((state) => state.userContext?.accountSettings?.userName);
    const { region, isAndroidWrapper, isIosWrapper } = useGlobalContext();
    const enrichedSignature = useRef<string | null>(null);

    useEffect(() => {
        const signature = [accountID, emailAddress, userName, region].join('|');

        if (isAndroidWrapper || isIosWrapper || !accountID || enrichedSignature.current === signature) {
            return undefined;
        }

        let attempts = 0;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const enrichWhenIdentified = async () => {
            const { getIdentifiedUserId, writeBrazeUserProfile } = await import('./braze-profile');

            if (cancelled) {
                return;
            }

            if (
                getIdentifiedUserId() === accountID &&
                writeBrazeUserProfile({ accountID, emailAddress, userName, region })
            ) {
                enrichedSignature.current = signature;
                return;
            }

            attempts += 1;

            if (attempts < MAX_ATTEMPTS) {
                timer = setTimeout(enrichWhenIdentified, RETRY_INTERVAL_MS);
            }
        };

        void enrichWhenIdentified();

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [accountID, emailAddress, userName, region, isAndroidWrapper, isIosWrapper]);
};
